import React, { useState } from 'react'
import {
  FileText,
  User,
  Mail,
  Globe,
  DollarSign,
  Calendar,
  CreditCard,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react'
import { mockApiService } from '../services/mockApiService'
import type { MockInvoiceFormData, MockInvoice } from '../services/mockApi'

interface InvoiceCreationFormProps {
  onInvoiceCreated?: (invoice: MockInvoice) => void
  onCancel?: () => void
  className?: string
}

export const InvoiceCreationForm: React.FC<InvoiceCreationFormProps> = ({
  onInvoiceCreated,
  onCancel,
  className = ''
}) => {
  const [formData, setFormData] = useState<MockInvoiceFormData>({
    customerName: '',
    customerEmail: '',
    customerCountry: '',
    amount: 0,
    currency: 'HKD',
    description: '',
    dueDate: '',
    settlementRail: 'FPS'
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const countries = [
    'United States', 'United Kingdom', 'Singapore', 'Japan', 'Australia', 
    'Canada', 'Germany', 'France', 'Netherlands', 'South Korea', 'Taiwan', 'Malaysia'
  ]

  const currencies = [
    { value: 'HKD', label: 'Hong Kong Dollar (HKD)' },
    { value: 'CNY', label: 'Chinese Yuan (CNY)' },
    { value: 'USD', label: 'US Dollar (USD)' }
  ]

  const settlementRails = [
    { value: 'FPS', label: 'FPS (Faster Payment System)', description: '15-minute HKD settlement' },
    { value: 'CIPS', label: 'CIPS (Cross-border Interbank Payment)', description: 'Same-day CNY settlement' },
    { value: 'e-CNY', label: 'e-CNY (Digital Yuan)', description: 'Instant digital currency settlement' }
  ]

  const handleInputChange = (field: keyof MockInvoiceFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setError(null)
  }

  const validateForm = (): boolean => {
    if (!formData.customerName.trim()) {
      setError('Customer name is required')
      return false
    }
    if (!formData.customerEmail.trim()) {
      setError('Customer email is required')
      return false
    }
    if (!formData.customerEmail.includes('@')) {
      setError('Please enter a valid email address')
      return false
    }
    if (!formData.customerCountry) {
      setError('Customer country is required')
      return false
    }
    if (formData.amount <= 0) {
      setError('Amount must be greater than 0')
      return false
    }
    if (!formData.description.trim()) {
      setError('Description is required')
      return false
    }
    if (!formData.dueDate) {
      setError('Due date is required')
      return false
    }
    
    const dueDate = new Date(formData.dueDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    if (dueDate < today) {
      setError('Due date cannot be in the past')
      return false
    }
    
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setIsSubmitting(true)
    setError(null)
    
    try {
      const response = await mockApiService.createInvoice(formData)
      
      if (response.success) {
        setSuccess(true)
        setTimeout(() => {
          onInvoiceCreated?.(response.data)
        }, 1500)
      } else {
        setError(response.error || 'Failed to create invoice')
      }
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Invoice creation error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getMinDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }

  if (success) {
    return (
      <div className={`invoice-creation-form ${className}`}>
        <div className="professional-card">
          <div className="professional-card__content">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="professional-heading-3 mb-2">Invoice Created Successfully!</h3>
              <p className="professional-text text-gray-600">
                Your invoice has been created and is ready to be sent to your customer.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`invoice-creation-form ${className}`}>
      <div className="professional-card">
        <div className="professional-card__header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="professional-card__title">Create New Invoice</h2>
              <p className="professional-card__subtitle">
                Generate a professional invoice with payment link for your overseas customer
              </p>
            </div>
          </div>
        </div>
        
        <div className="professional-card__content">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Information */}
            <div className="invoice-creation-form__section">
              <h3 className="professional-heading-3 mb-4">Customer Information</h3>
              
              <div className="professional-grid professional-grid--cols-2">
                <div className="professional-form-group">
                  <label className="professional-form-label">
                    <User className="w-4 h-4" />
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => handleInputChange('customerName', e.target.value)}
                    className="professional-form-input"
                    placeholder="Enter customer name"
                    required
                  />
                </div>
                
                <div className="professional-form-group">
                  <label className="professional-form-label">
                    <Mail className="w-4 h-4" />
                    Customer Email *
                  </label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                    className="professional-form-input"
                    placeholder="customer@example.com"
                    required
                  />
                </div>
              </div>
              
              <div className="professional-form-group">
                <label className="professional-form-label">
                  <Globe className="w-4 h-4" />
                  Customer Country *
                </label>
                <select
                  value={formData.customerCountry}
                  onChange={(e) => handleInputChange('customerCountry', e.target.value)}
                  className="professional-form-select"
                  required
                >
                  <option value="">Select country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="invoice-creation-form__section">
              <h3 className="professional-heading-3 mb-4">Invoice Details</h3>
              
              <div className="professional-grid professional-grid--cols-2">
                <div className="professional-form-group">
                  <label className="professional-form-label">
                    <DollarSign className="w-4 h-4" />
                    Amount *
                  </label>
                  <input
                    type="number"
                    value={formData.amount || ''}
                    onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                    className="professional-form-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                
                <div className="professional-form-group">
                  <label className="professional-form-label">
                    Currency *
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    className="professional-form-select"
                    required
                  >
                    {currencies.map(currency => (
                      <option key={currency.value} value={currency.value}>
                        {currency.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="professional-form-group">
                <label className="professional-form-label">
                  <FileText className="w-4 h-4" />
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="professional-form-textarea"
                  placeholder="Describe the goods or services provided"
                  rows={3}
                  required
                />
              </div>
              
              <div className="professional-form-group">
                <label className="professional-form-label">
                  <Calendar className="w-4 h-4" />
                  Due Date *
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  className="professional-form-input"
                  min={getMinDate()}
                  required
                />
              </div>
            </div>

            {/* Settlement Configuration */}
            <div className="invoice-creation-form__section">
              <h3 className="professional-heading-3 mb-4">Settlement Configuration</h3>
              
              <div className="professional-form-group">
                <label className="professional-form-label">
                  <CreditCard className="w-4 h-4" />
                  Settlement Rail *
                </label>
                <div className="space-y-3">
                  {settlementRails.map(rail => (
                    <label key={rail.value} className="professional-radio-option">
                      <input
                        type="radio"
                        name="settlementRail"
                        value={rail.value}
                        checked={formData.settlementRail === rail.value}
                        onChange={(e) => handleInputChange('settlementRail', e.target.value)}
                        className="professional-radio-input"
                      />
                      <div className="professional-radio-content">
                        <div className="professional-radio-title">{rail.label}</div>
                        <div className="professional-radio-description">{rail.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="professional-alert professional-alert--error">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            {/* Form Actions */}
            <div className="invoice-creation-form__actions">
              <button
                type="button"
                onClick={onCancel}
                className="professional-btn professional-btn--secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="professional-btn professional-btn--primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Invoice...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Create Invoice
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}